import { supabase } from '../../lib/supabase';
import type { Database, Json } from '../../types/database';

type Tables = Database['public']['Tables'];
type Enums = Database['public']['Enums'];

export type ExamType = Enums['exam_type'];
export type SectionKind = Enums['section_kind'];
export type QuestionKind = Enums['question_kind'];
export type TestStatus = Enums['test_status'];

export type TestRow = Tables['tests']['Row'];
export type TestSectionRow = Tables['test_sections']['Row'];
export type SectionPassageRow = Tables['section_passages']['Row'];
export type QuestionRow = Tables['questions']['Row'];
export type AnswerKeyRow = Tables['answer_keys']['Row'];

export const QBANK_QUERY_KEYS = {
  tests: ['admin', 'qbank', 'tests'] as const,
  test: (id: string) => ['admin', 'qbank', 'test', id] as const,
  sections: (testId: string) => ['admin', 'qbank', 'sections', testId] as const,
  section: (sectionId: string) => ['admin', 'qbank', 'section', sectionId] as const,
  passages: (sectionId: string) => ['admin', 'qbank', 'passages', sectionId] as const,
  questions: (sectionId: string) => ['admin', 'qbank', 'questions', sectionId] as const,
  answerKeys: (sectionId: string) => ['admin', 'qbank', 'answer_keys', sectionId] as const,
};

function unwrap<T>(data: T | null, error: { message: string } | null): T {
  if (error !== null) throw new Error(error.message);
  if (data === null) throw new Error('Empty response from Supabase');
  return data;
}

// ---------------------------------------------------------------------------
// tests
// ---------------------------------------------------------------------------

/** A global test row plus the distinct section kinds it holds, so the list
 *  page can offer a Reading/Listening/Writing/Speaking filter without every
 *  caller of `getTest`/`updateTest` having to carry the extra field. */
export interface GlobalTestListRow extends TestRow {
  section_kinds: SectionKind[];
}

export async function listGlobalTests(): Promise<GlobalTestListRow[]> {
  const { data, error } = await supabase
    .from('tests')
    .select('*')
    .is('institute_id', null)
    .order('created_at', { ascending: false });
  const rows = unwrap(data, error);
  if (rows.length === 0) return [];

  // Second round trip instead of an embedded `test_sections(kind)` select —
  // keeps this on the same plain `.select()` shape the rest of the file
  // already relies on for typed results, rather than reaching for PostgREST
  // resource embedding (untested against this project's generated types).
  const { data: sectionRows, error: sectionError } = await supabase
    .from('test_sections')
    .select('test_id, kind')
    .in(
      'test_id',
      rows.map((r) => r.id),
    );
  if (sectionError !== null) throw new Error(sectionError.message);

  const kindsByTest = new Map<string, SectionKind[]>();
  for (const sec of sectionRows ?? []) {
    const kinds = kindsByTest.get(sec.test_id) ?? [];
    if (!kinds.includes(sec.kind)) kinds.push(sec.kind);
    kindsByTest.set(sec.test_id, kinds);
  }

  return rows.map((row) => ({ ...row, section_kinds: kindsByTest.get(row.id) ?? [] }));
}

export async function getTest(testId: string): Promise<TestRow> {
  const { data, error } = await supabase.from('tests').select('*').eq('id', testId).maybeSingle();
  const row = unwrap(data, error);
  if (row === null) throw new Error('Test not found');
  return row;
}

export interface CreateTestArgs {
  title: string;
  description: string | null;
  exam_type: ExamType;
  total_duration_minutes: number;
}

export async function createGlobalTest(args: CreateTestArgs): Promise<TestRow> {
  const { data, error } = await supabase
    .from('tests')
    .insert({
      institute_id: null,
      title: args.title,
      description: args.description,
      exam_type: args.exam_type,
      total_duration_minutes: args.total_duration_minutes,
      status: 'draft',
    })
    .select('*')
    .single();
  return unwrap(data, error);
}

export interface UpdateTestArgs {
  testId: string;
  patch: Partial<{
    title: string;
    description: string | null;
    exam_type: ExamType;
    total_duration_minutes: number;
    status: TestStatus;
  }>;
}

export async function updateTest({ testId, patch }: UpdateTestArgs): Promise<TestRow> {
  const { data, error } = await supabase
    .from('tests')
    .update(patch)
    .eq('id', testId)
    .select('*')
    .single();
  return unwrap(data, error);
}

// ---------------------------------------------------------------------------
// sections + passages
// ---------------------------------------------------------------------------

export async function listSections(testId: string): Promise<TestSectionRow[]> {
  const { data, error } = await supabase
    .from('test_sections')
    .select('*')
    .eq('test_id', testId)
    .order('display_order', { ascending: true });
  return unwrap(data, error);
}

export async function getSection(sectionId: string): Promise<TestSectionRow> {
  const { data, error } = await supabase
    .from('test_sections')
    .select('*')
    .eq('id', sectionId)
    .maybeSingle();
  const row = unwrap(data, error);
  if (row === null) throw new Error('Section not found');
  return row;
}

const DEFAULT_SECTION_DURATION_MIN: Record<SectionKind, number> = {
  listening: 30,
  reading: 60,
  writing: 60,
  speaking: 15,
};

export interface CreateSectionArgs {
  testId: string;
  kind: SectionKind;
  displayOrder: number;
  instructions_md?: string | null;
}

export async function createSection(args: CreateSectionArgs): Promise<TestSectionRow> {
  const { data, error } = await supabase
    .from('test_sections')
    .insert({
      test_id: args.testId,
      kind: args.kind,
      display_order: args.displayOrder,
      duration_minutes: DEFAULT_SECTION_DURATION_MIN[args.kind],
      instructions_md: args.instructions_md ?? null,
    })
    .select('*')
    .single();
  return unwrap(data, error);
}

export interface UpdateSectionArgs {
  sectionId: string;
  patch: Partial<{
    duration_minutes: number;
    instructions_md: string | null;
  }>;
}

export async function updateSection({
  sectionId,
  patch,
}: UpdateSectionArgs): Promise<TestSectionRow> {
  const { data, error } = await supabase
    .from('test_sections')
    .update(patch)
    .eq('id', sectionId)
    .select('*')
    .single();
  return unwrap(data, error);
}

export async function listPassages(sectionId: string): Promise<SectionPassageRow[]> {
  const { data, error } = await supabase
    .from('section_passages')
    .select('*')
    .eq('section_id', sectionId)
    .order('display_order', { ascending: true });
  return unwrap(data, error);
}

export interface UpsertPassageArgs {
  // Optional fields are typed `T | undefined` (not `T?`) so that callers
  // working under exactOptionalPropertyTypes can explicitly forward undefined
  // without a workaround.
  passageId?: string | undefined;
  sectionId: string;
  displayOrder: number;
  title: string | null;
  body_md: string | null;
  audio_path?: string | null | undefined;
  audio_duration_seconds?: number | null | undefined;
}

export async function upsertPassage(args: UpsertPassageArgs): Promise<SectionPassageRow> {
  if (args.passageId === undefined) {
    const { data, error } = await supabase
      .from('section_passages')
      .insert({
        section_id: args.sectionId,
        display_order: args.displayOrder,
        title: args.title,
        body_md: args.body_md,
        audio_path: args.audio_path ?? null,
        audio_duration_seconds: args.audio_duration_seconds ?? null,
      })
      .select('*')
      .single();
    return unwrap(data, error);
  }
  const patch: Record<string, unknown> = {
    display_order: args.displayOrder,
    title: args.title,
    body_md: args.body_md,
  };
  if (args.audio_path !== undefined) patch.audio_path = args.audio_path;
  if (args.audio_duration_seconds !== undefined)
    patch.audio_duration_seconds = args.audio_duration_seconds;

  const { data, error } = await supabase
    .from('section_passages')
    .update(patch)
    .eq('id', args.passageId)
    .select('*')
    .single();
  return unwrap(data, error);
}

// ---------------------------------------------------------------------------
// questions + answer_keys
// ---------------------------------------------------------------------------

export async function listQuestions(sectionId: string): Promise<QuestionRow[]> {
  const { data, error } = await supabase
    .from('questions')
    .select('*')
    .eq('section_id', sectionId)
    .order('display_order', { ascending: true });
  return unwrap(data, error);
}

export interface CreateQuestionArgs {
  sectionId: string;
  passageId?: string | null;
  kind: QuestionKind;
  displayOrder: number;
  prompt_md: string;
  payload_jsonb: Json;
  max_points?: number;
}

export async function createQuestion(args: CreateQuestionArgs): Promise<QuestionRow> {
  const { data, error } = await supabase
    .from('questions')
    .insert({
      section_id: args.sectionId,
      passage_id: args.passageId ?? null,
      kind: args.kind,
      display_order: args.displayOrder,
      prompt_md: args.prompt_md,
      payload_jsonb: args.payload_jsonb,
      max_points: args.max_points ?? 1,
    })
    .select('*')
    .single();
  return unwrap(data, error);
}

export interface UpdateQuestionArgs {
  questionId: string;
  patch: Partial<{
    prompt_md: string;
    payload_jsonb: Json;
    display_order: number;
    passage_id: string | null;
    max_points: number;
  }>;
}

export async function updateQuestion({
  questionId,
  patch,
}: UpdateQuestionArgs): Promise<QuestionRow> {
  const { data, error } = await supabase
    .from('questions')
    .update(patch)
    .eq('id', questionId)
    .select('*')
    .single();
  return unwrap(data, error);
}

export async function deleteQuestion(questionId: string): Promise<void> {
  const { error } = await supabase.from('questions').delete().eq('id', questionId);
  if (error !== null) throw new Error(error.message);
}

// answer_keys is keyed by question_id (1:1). The table has its OWN
// INSERT/UPDATE/DELETE policies (`answer_keys_admin_*`, added in the
// 202606072300 migration) that walk the question → section → test chain to
// allow superadmin and institute-admin writes. Policies on `questions` do
// NOT transitively apply to `answer_keys` — every table needs its own.
//
// SELECT is intentionally absent so only service_role can read keys; that's
// what keeps Phase 20's submit-attempt grader the single path. The admin UI
// therefore never fetches existing keys — every save is a fresh upsert.
export async function upsertAnswerKey(args: {
  questionId: string;
  key_pattern: string;
  scoring_notes?: string | null;
}): Promise<void> {
  const { error } = await supabase.from('answer_keys').upsert({
    question_id: args.questionId,
    key_pattern: args.key_pattern,
    scoring_notes: args.scoring_notes ?? null,
  });
  if (error !== null) throw new Error(error.message);
}
